import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { sectionBadge } from '../../utils/animationUtils';
import { PiGraduationCap, PiMapPin, PiDownloadSimple } from 'react-icons/pi';

gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
    const containerRef = useRef(null);
    const ghostRef = useRef(null);
    const photoWrapRef = useRef(null);
    const photoImgRef = useRef(null);
    const nametagRef = useRef(null);
    const annotationRef = useRef(null);
    const downloadBtnRef = useRef(null);
    const downloadIconRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states via GSAP (not CSS)
            gsap.set('.about-badge', { opacity: 0, scale: 0 });
            gsap.set('.about-heading-word', { opacity: 0, y: 50 });
            gsap.set('.about-text', { opacity: 0, y: 30 });
            gsap.set('.about-pill', { opacity: 0, scale: 0 });
            gsap.set(photoWrapRef.current, { clipPath: 'inset(0 0 100% 0)' });
            gsap.set(nametagRef.current, { opacity: 0, x: -40, rotation: 10 });
            gsap.set(annotationRef.current, { opacity: 0, x: -20 });
            gsap.set(downloadBtnRef.current, { opacity: 0, y: 30 });

            sectionBadge('.about-badge');

            // Photo clip wipe from bottom
            gsap.to(photoWrapRef.current, {
                clipPath: 'inset(0 0 0% 0)',
                duration: 0.9,
                ease: 'power4.inOut',
                scrollTrigger: { trigger: photoWrapRef.current, start: 'top 92%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Photo image hover (on photo wrap)
            if (photoImgRef.current) {
                photoWrapRef.current.addEventListener('mouseenter', () => {
                    gsap.to(photoImgRef.current, { scale: 1.04, duration: 0.4, ease: 'power2.out' });
                });
                photoWrapRef.current.addEventListener('mouseleave', () => {
                    gsap.to(photoImgRef.current, { scale: 1.0, duration: 0.6, ease: 'power3.out' });
                });
            }

            // Nametag: from x:-40 rotation:10 → x:0 rotation:3
            gsap.to(nametagRef.current, {
                opacity: 1, x: 0, rotation: 3, duration: 0.8, ease: 'back.out(1.5)',
                scrollTrigger: { trigger: nametagRef.current, start: 'top 92%', toggleActions: 'play none none reverse', delay: 0.3, fastScrollEnd: true }
            });

            // Annotation fade in
            gsap.to(annotationRef.current, {
                opacity: 1, x: 0, duration: 0.6, ease: 'power3.out',
                scrollTrigger: { trigger: annotationRef.current, start: 'top 92%', toggleActions: 'play none none reverse', delay: 0.5, fastScrollEnd: true }
            });

            // Nametag hover
            if (nametagRef.current) {
                nametagRef.current.addEventListener('mouseenter', () => {
                    gsap.to(nametagRef.current, { rotation: 0, scale: 1.03, duration: 0.25, ease: 'power2.out' });
                });
                nametagRef.current.addEventListener('mouseleave', () => {
                    gsap.to(nametagRef.current, { rotation: 3, scale: 1, ease: 'elastic.out(1,0.5)', duration: 0.6 });
                });
            }

            // Heading per word stagger
            gsap.to('.about-heading-word', {
                opacity: 1, y: 0, stagger: 0.1, duration: 0.7, ease: 'power3.out',
                scrollTrigger: { trigger: '.about-heading-word', start: 'top 95%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Paragraphs stagger
            gsap.to('.about-text', {
                opacity: 1, y: 0, stagger: 0.15, duration: 0.7, ease: 'power3.out',
                scrollTrigger: { trigger: '.about-text', start: 'top 88%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Pills elastic
            gsap.to('.about-pill', {
                opacity: 1, scale: 1, stagger: 0.1, ease: 'elastic.out(1,0.5)', duration: 0.7,
                scrollTrigger: { trigger: '.about-pill', start: 'top 92%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Pill hover
            const pills = document.querySelectorAll('.about-pill');
            pills.forEach(pill => {
                pill.addEventListener('mouseenter', () => {
                    gsap.to(pill, { backgroundColor: '#FFE600', duration: 0.2, ease: 'power2.out' });
                });
                pill.addEventListener('mouseleave', () => {
                    gsap.to(pill, { backgroundColor: 'white', duration: 0.3, ease: 'power2.out' });
                });
            });

            // Download button appear
            gsap.to(downloadBtnRef.current, {
                opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
                scrollTrigger: { trigger: downloadBtnRef.current, start: 'top 92%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Download button hover
            if (downloadBtnRef.current) {
                downloadBtnRef.current.addEventListener('mouseenter', () => {
                    gsap.to(downloadBtnRef.current, {
                        x: 6, y: 6, backgroundColor: '#FFE600', color: '#111', duration: 0.15, ease: 'power2.out'
                    });
                    gsap.to(downloadIconRef.current, { rotation: '+=360', duration: 0.5, ease: 'power2.out' });
                });
                downloadBtnRef.current.addEventListener('mouseleave', () => {
                    gsap.to(downloadBtnRef.current, {
                        x: 0, y: 0, backgroundColor: '#111', color: '#FFE600',
                        ease: 'elastic.out(1,0.5)', duration: 0.5
                    });
                });
            }

            // Ghost text parallax
            if (ghostRef.current) {
                gsap.to(ghostRef.current, {
                    yPercent: -30,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true,
                    }
                });
            }
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section id="about" ref={containerRef} className="section-dot-bg py-24 font-jakarta bg-[#F2EFE9]" style={{ overflow: 'hidden' }}>
            {/* Ghost Watermark */}
            <span ref={ghostRef} className="ghost-text" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>ABOUT</span>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

                    {/* Left Column */}
                    <div className="flex flex-col gap-6">
                        {/* Photo */}
                        <div
                            ref={photoWrapRef}
                            className="relative inline-block self-start"
                            style={{
                                width: '100%',
                                maxWidth: 340,
                                aspectRatio: '4/5',
                                borderRadius: 12,
                                cursor: 'default',
                                overflow: 'visible',
                            }}
                        >
                            {/* Yellow offset accent block */}
                            <div
                                style={{
                                    position: 'absolute',
                                    width: '75%',
                                    height: '65%',
                                    background: '#FFE600',
                                    border: '3px solid #111',
                                    boxShadow: '8px 8px 0 #111',
                                    borderRadius: 10,
                                    bottom: 0,
                                    right: 0,
                                    zIndex: 0,
                                }}
                            />
                            <img
                                ref={photoImgRef}
                                src="/assets/profile.png"
                                alt="Ahmad Haziq"
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    borderRadius: 12,
                                    transformOrigin: 'center center',
                                }}
                            />
                        </div>

                        {/* Nametag */}
                        <div
                            ref={nametagRef}
                            style={{ maxWidth: 300, cursor: 'default' }}
                        >
                            <div
                                style={{
                                    backgroundColor: '#111',
                                    color: 'white',
                                    border: '2px solid #111',
                                    borderRadius: 12,
                                    padding: '16px 24px',
                                    boxShadow: '4px 4px 0 #FFE600',
                                }}
                            >
                                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: '0.7rem', color: '#FFE600', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 4 }}>ID Card</p>
                                <h3 style={{ fontWeight: 900, fontSize: '1.2rem', color: 'white' }}>Ahmad Haziq M.W.</h3>
                                <p style={{ color: '#aaa', fontSize: '0.9rem', fontWeight: 500, marginTop: 4 }}>Software Engineer</p>
                            </div>
                        </div>

                        {/* Annotation */}
                        <div
                            ref={annotationRef}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 24,
                                transform: 'rotate(-2deg)'
                            }}
                        >
                            <span
                                style={{
                                    width: 40,
                                    height: 1,
                                    backgroundColor: '#888'
                                }}
                            />
                            <span
                                style={{
                                    fontFamily: "'Space Mono', monospace",
                                    fontSize: 11,
                                    color: '#888',
                                    fontStyle: 'italic'
                                }}
                            >
                                ← that's me, since 2022
                            </span>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div>
                        <div
                            className="about-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full mb-6"
                            style={{ boxShadow: '3px 3px 0 #111', fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}
                        >
                            <span className="w-2 h-2 rounded-full bg-[#FFE600] inline-block" />
                            ABOUT ME
                        </div>

                        <h2
                            className="mb-6"
                            style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.0, fontSize: 'var(--text-h1)', color: 'var(--ink)' }}
                        >
                            <span className="about-heading-word inline-block mr-3">I'm Ahmad</span>
                            <span className="about-heading-word inline-block" style={{ color: '#FF3366', fontStyle: 'italic' }}>Haziq.</span>
                        </h2>

                        {/* Pull quote */}
                        <p
                            className="about-text"
                            style={{
                                fontFamily: "'Syne', sans-serif",
                                fontWeight: 700,
                                fontStyle: 'italic',
                                fontSize: 'clamp(18px, 2vw, 22px)',
                                color: '#111',
                                borderLeft: '4px solid #FFE600',
                                paddingLeft: 20,
                                marginBottom: 24,
                                lineHeight: 1.4,
                                maxWidth: 'none',
                            }}
                        >
                            &ldquo;I build things that work.&rdquo;
                        </p>

                        <p
                            className="about-text mb-6"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-base)', lineHeight: 'var(--leading-loose)', color: 'var(--ink-muted)', maxWidth: '55ch' }}
                        >
                            A results-oriented <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>Full-Stack Developer</strong> specializing in building scalable web applications. Passionate about clean architecture, seamless UX, and solving real business problems with code.
                        </p>

                        <p
                            className="about-text mb-10"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-base)', lineHeight: 'var(--leading-loose)', color: 'var(--ink-muted)', maxWidth: '55ch' }}
                        >
                            With 2+ years of professional experience spanning freelance, agency, and institutional projects, I bring both technical depth and product thinking to every engagement.
                        </p>

                        <p
                            className="about-text mb-10"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-base)', lineHeight: 'var(--leading-loose)', color: 'var(--ink-muted)', maxWidth: '55ch' }}
                        >
                            Beyond code, I manage the full deployment lifecycle — Linux VPS provisioning, Nginx configuration, SSL/security, and database optimization. Your application isn't just built; it's <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>production-ready</strong>.
                        </p>

                        {/* Detail Pills */}
                        <div className="flex flex-wrap gap-3 mb-10">
                            <div
                                className="about-pill inline-flex items-center gap-2 bg-white border-2 border-[#111] px-4 py-2.5"
                                style={{ borderRadius: 999, boxShadow: '3px 3px 0 #111', cursor: 'default', fontFamily: "var(--font-body)", fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}
                            >
                                <PiGraduationCap weight="bold" size={16} />
                                S1 Informatics — Asia Cyber University
                            </div>
                            <div
                                className="about-pill inline-flex items-center gap-2 bg-white border-2 border-[#111] px-4 py-2.5"
                                style={{ borderRadius: 999, boxShadow: '3px 3px 0 #111', cursor: 'default', fontFamily: "var(--font-body)", fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}
                            >
                                <PiMapPin weight="bold" size={16} />
                                East Java, Indonesia
                            </div>
                        </div>

                        {/* Download Button — inline styled, no old component */}
                        <a href="/assets/CV_Ahmad_Haziq.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <button
                                ref={downloadBtnRef}
                                style={{
                                    padding: '16px 32px',
                                    backgroundColor: '#111',
                                    color: '#FFE600',
                                    border: '3px solid #111',
                                    boxShadow: '6px 6px 0 #FFE600',
                                    borderRadius: 8,
                                    fontFamily: "'Space Mono', monospace",
                                    fontWeight: 700,
                                    fontSize: 'var(--text-sm)',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    cursor: 'pointer',
                                }}
                            >
                                Download CV
                                <span ref={downloadIconRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <PiDownloadSimple weight="bold" size={18} />
                                </span>
                            </button>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutSection;